import { MarketSignal } from '@/modules/market-signals/model/market-signals/market-signal';
import dayjs from 'dayjs';
import MarketSignalSourceBadge, {
  MarketSignalSourceText,
} from '@/modules/market-signals/components/market-signals/market-signal-source-badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faCircleInfo,
  faEnvelope,
  faImage,
  faUpRightFromSquare,
  faVideo,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { useContext, useEffect, useState } from 'react';
import { MarketSignalContext } from '@/modules/market-signals/contexts/market-signal-context';
import { MarketSignalStreamAnalysisTopic } from '@/modules/market-signals/model/market-signals/market-signal-stream-analysis';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MarketSignalAlarm } from '@/modules/market-signals/model/market-signals/market-signal-alarm';
import MarketSignalAlarmBadge from '@/modules/market-signals/components/market-signals/alarms/market-signal-alarm-badge';

import MarketSignalClipboardButton from '@/modules/market-signals/components/market-signals/market-signal-clipboard-button';
import MarketSignalTranslationModal from '@/modules/market-signals/components/market-signals/scroller/market-signal-translation-modal';
import MarketSignalSourceButton from '@/modules/market-signals/components/market-signals/market-signal-source-button';

function extractContent(html: string) {
  const span = document.createElement('span');
  span.innerHTML = html;
  let text = [span.innerText].toString().replace(/\s+/g, ' ');

  if (text.length > 120) text = text.substring(0, 120) + ' ... ';

  return text;
}

const isOld = (signal: MarketSignal) => {
  return !dayjs(signal.dateCreated).isSame(dayjs(), 'day');
};

export function MarketSignalEntryHeader({
  signal,
  mentionedInTopic,
}: {
  signal: MarketSignal;
  mentionedInTopic?: boolean;
}) {
  const { alarms } = useContext(MarketSignalContext);

  const myAlarms: MarketSignalAlarm[] = alarms?.filter(v => v.signalId === signal._id) || [];

  return (
    <>
      <div className={`d-flex justify-content-between`}>
        <div className={'font-monospace text-muted'}>
          <strong className={'text-body'}>{dayjs(signal.dateCreated).format('h:mma')}</strong> -{' '}
          <MarketSignalSourceText source={signal.sourcePlatform}>
            {signal.infoSources.map(s => s.name).join(', ')}
          </MarketSignalSourceText>{' '}
          <MarketSignalSourceButton
            signal={signal}
            entry={MarketSignalScrollerEntry}
            key={`b-${signal._id}`}
          />
        </div>

        <div>
          <MarketSignalAlarmBadge alarms={myAlarms} />
          {mentionedInTopic && <FontAwesomeIcon icon={faCircleInfo} className={'text-info'} />}{' '}
          <MarketSignalSourceBadge source={signal.sourcePlatform} />{' '}
          <span className="px-1">
            <MarketSignalClipboardButton signal={signal} />
          </span>
        </div>
      </div>
      {isOld(signal) && (
        <div className={' font-monospace mb-1 text-warning-emphasis'}>
          <strong>{dayjs(signal.dateCreated).format('MMM D')}</strong>{' '}
          <span>({dayjs(signal.dateCreated).fromNow()})</span>{' '}
        </div>
      )}
    </>
  );
}

export default function MarketSignalScrollerEntry({
  signal,
  topics = [],
  interactive = false,
  showTranslationLink = true,
}: {
  signal: MarketSignal;
  topics?: MarketSignalStreamAnalysisTopic[];
  interactive?: boolean;
  showTranslationLink?: boolean;
}) {
  const [summary, setSummary] = useState<string>();
  const { setSelectedItem, acknowledgeAlarms, alarmsForSignal, setHoveredItem, hoveredItem } =
    useContext(MarketSignalContext);

  const iconMap: Record<string, IconDefinition> = {
    image: faImage,
    photo: faImage,
    video: faVideo,
    animated_gif: faVideo,
    article: faEnvelope,
  };

  const [showTranslation, setShowTranslation] = useState<boolean>(false);

  useEffect(() => {
    if (signal.summary) {
      setSummary(signal.summary);
    } else if (signal.body) {
      setSummary(extractContent(signal.body));
    }
  }, [signal]);

  const alarms = alarmsForSignal(signal._id);

  const renderTooltip = (props: { signal: MarketSignal }) =>
    alarms.length > 0 ? (
      <Tooltip id="button-tooltip" {...props} className={'text-start'}>
        <h3>Alarms</h3>
        {alarms.map(a => (
          <div className={'mb-3'}>
            <FontAwesomeIcon icon={faBell} className={'text-danger'} /> {a.reason}
          </div>
        ))}
      </Tooltip>
    ) : (
      <></>
    );

  const onMouseEnter = () => {
    interactive &&
      acknowledgeAlarms(alarms.filter(value => !value.acknowledged).map(value => value._id));

    setHoveredItem(signal);
  };

  const onMouseLeave = () => {
    if (hoveredItem?._id === signal._id) {
      setHoveredItem(undefined);
    }
  };

  const openMedia = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    e.stopPropagation();
    e.preventDefault();

    window.open(url, 'ms-popup', 'width=600,height=400');
  };

  return (
    <OverlayTrigger overlay={injected => renderTooltip({ ...injected, signal })}>
      <div
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={isOld(signal) ? 'bg-body-tertiary' : ''}
      >
        <MarketSignalEntryHeader signal={signal} />
        <div
          className={summary && 'fw-bold'}
          dangerouslySetInnerHTML={{ __html: signal.headline }}
        />
        <div>
          {summary && (
            <span className={'text-muted'} dangerouslySetInnerHTML={{ __html: summary }} />
          )}
          {signal.body && (
            <div>
              <a
                href={'#a'}
                onClick={event => {
                  event.stopPropagation();
                  event.preventDefault();
                  setSelectedItem(signal);
                }}
              >
                Read More
              </a>
            </div>
          )}
        </div>

        {signal.mediaAttachments?.length > 0 && (
          <div className={'d-flex justify-content-between'}>
            <div>
              {' '}
              {/*key added here*/}
              {signal.mediaAttachments.map((value, index) => (
                <div key={index}>
                  <a href={`#a`} onClick={event => openMedia(event, value.url)}>
                    <FontAwesomeIcon icon={iconMap[value.type] || faImage} /> Open Media (
                    {value.type})
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={'d-flex justify-content-between font-monospace text-info'}>
          <div>
            {showTranslationLink &&
              signal.originalLanguage !== 'en' &&
              signal.originalLanguage !== 'zxx' && (
                <a href={'#a'} onClick={event => setShowTranslation(true)}>
                  Translated ({signal.originalLanguage})
                </a>
              )}
          </div>

          <div>
            {signal.url && (
              <div>
                <a href={signal.url} target={'_blank'} rel={'noreferrer'}>
                  Original <FontAwesomeIcon icon={faUpRightFromSquare} />
                </a>
              </div>
            )}
          </div>
        </div>
        {showTranslation && (
          <MarketSignalTranslationModal
            onClose={() => setShowTranslation(false)}
            signal={signal}
            entry={MarketSignalScrollerEntry}
          />
        )}
      </div>
    </OverlayTrigger>
  );
}
